import { useCallback, useEffect, type MouseEvent } from 'react';
import { v4 as uuid } from 'uuid';
import { p, text, inlineCard, doc, mention, rule } from '@atlaskit/adf-utils/builders';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	COMMENT_VISIBILITY_PUBLIC,
	COMMENT_VISIBILITY_TYPE_PUBLIC,
} from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';
import { NEW_COMMENT_ID } from '@atlassian/jira-issue-view-store/src/selectors/comment-constants.tsx';
import { smoothScrollIntoViewIfNeeded } from '@atlassian/jira-issue-view-common-utils/src/scroll/index.tsx';
import {
	type UIAnalyticsEvent,
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { isOnlyWhitespaceAdf, type ADF } from '@atlassian/jira-rich-content/src/index.tsx';
import { isJsmLoomExperimentEnabled } from '@atlassian/jira-loom-jsm-agent-response/src/common/utils.tsx';
import type { User } from '@atlassian/jira-issue-shared-types/src/common/types/user-type.tsx';
import type { CreatedIssueWithIssueData } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { createFullIssueUrl } from '@atlassian/jira-issue-view-store/src/selectors/breadcrumbs-selector.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import {
	ACTION_KEY_PARAM,
	REPLY_TO_COMMENT_ACTION_TYPE,
} from '@atlassian/jira-issue-shared-types/src/common/types/query-params.tsx';
import { areCommentVisibilitiesEqual } from '../comment-visibility/view/utils.tsx';
import messages from '../messages.tsx';
import { UNREAD_COMMENT_LOZENGE_VIEWED_KEY, localStorageProvider } from '../constants.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';

export const useAuthorAdditionalInfo = () => {
	const { formatMessage } = useIntl();
	const { jsdAuthorCanSeeRequest } = useCommentContext();
	const isRequestParticipant = jsdAuthorCanSeeRequest === false;
	if (expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)) {
		return isRequestParticipant ? ` ${formatMessage(messages.notRequestParticipant)}` : '';
	}
	return isRequestParticipant ? formatMessage(messages.notRequestParticipant) : '';
};
export const useAuthorDisplayName = () => {
	const { author } = useCommentContext();
	const { formatMessage } = useIntl();
	if (author?.displayName) {
		return author.displayName;
	}
	return formatMessage(messages.anonymous);
};
export const useUpdateAuthorDisplayName = () => {
	const { updateAuthor } = useCommentContext();
	const { formatMessage } = useIntl();
	if (updateAuthor?.displayName) {
		return updateAuthor.displayName;
	}
	return formatMessage(messages.anonymous);
};
export const useUnreadComment = () => {
	const { author, projectType, createdDate, latestCommentViewInteractedTime, currentUserAaid } =
		useCommentContext();
	if (
		(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
		fg('thor_issue_view_realtime_updates_gate')
	) {
		return (
			(author?.id !== currentUserAaid &&
				latestCommentViewInteractedTime &&
				new Date(latestCommentViewInteractedTime).getTime() < new Date(createdDate).getTime()) ||
			false
		);
	}
	return false;
};
export const useRestrictedToText = () => {
	const { formatMessage } = useIntl();
	const { visibility, isInternal } = useCommentContext();
	if (isInternal) {
		const restrictedToText =
			visibility.type === COMMENT_VISIBILITY_TYPE_PUBLIC
				? `${formatMessage(messages.internalNote)}`
				: `${visibility.value} • ${formatMessage(messages.internalNote)}`;
		return restrictedToText;
	}
	return visibility?.value || '';
};
export const useUnreadCommentLozengeState = () => {
	const {
		projectType,
		commentId,
		isActivityFeedInViewPort,
		showNewUnreadCommentLozenge,
		setShowNewUnreadCommentLozenge,
		topMostUnreadCommentId,
	} = useCommentContext();
	const isUnreadComment = useUnreadComment();
	useEffect(() => {
		let unreadCommentLozengeTimeout: NodeJS.Timeout | undefined;
		if (
			(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
			isActivityFeedInViewPort &&
			showNewUnreadCommentLozenge &&
			isUnreadComment &&
			expVal('thor_issue_view_realtime_updates_experiment', 'isEnabled', false)
		) {
			localStorageProvider.set(UNREAD_COMMENT_LOZENGE_VIEWED_KEY, true);
			unreadCommentLozengeTimeout = setTimeout(() => {
				setShowNewUnreadCommentLozenge?.(false);
			}, 5000);
		}
		if (
			(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
			commentId !== topMostUnreadCommentId &&
			showNewUnreadCommentLozenge &&
			isUnreadComment &&
			expVal('thor_issue_view_realtime_updates_experiment', 'isEnabled', false)
		) {
			setShowNewUnreadCommentLozenge?.(false);
		}
		return () => {
			if (unreadCommentLozengeTimeout) {
				clearTimeout(unreadCommentLozengeTimeout);
				unreadCommentLozengeTimeout = undefined;
			}
		};
	}, [
		commentId,
		isActivityFeedInViewPort,
		isUnreadComment,
		projectType,
		setShowNewUnreadCommentLozenge,
		showNewUnreadCommentLozenge,
		topMostUnreadCommentId,
	]);
};
export const useSmoothScrollIntoView = () => {
	const { commentId, optimisticCommentContainer, isOptimistic } = useCommentContext();
	useEffect(() => {
		if (isOptimistic && commentId.includes(NEW_COMMENT_ID) && optimisticCommentContainer) {
			smoothScrollIntoViewIfNeeded(optimisticCommentContainer, {
				scrollMode: 'always',
				behavior: 'smooth',
			});
		}
	}, [commentId, isOptimistic, optimisticCommentContainer]);
};
export const useScrollCommentIntoView = () => {
	const {
		commentId,
		commentPermalinkContainer,
		hasScrolledPermalink,
		setScrollStatus,
		setScrollStatusForRealTimeUpdate,
		commentScrollStatusForRealTimeUpdates,
		topMostUnreadCommentId,
		unreadCommentElement,
		createAnalyticsEvent,
		projectType,
		shouldScrollAfterAsyncComponentsLoaded,
	} = useCommentContext();
	const isUnreadComment = useUnreadComment();
	useEffect(() => {
		// hasScrolledPermalink ensures we only scroll once on initial page load.
		// ensure we don't scroll if we resize after initial load.
		// shouldScrollAfterAsyncComponentsLoaded used to ensure we only scroll when all async data is finished fetching.
		if (!hasScrolledPermalink && shouldScrollAfterAsyncComponentsLoaded) {
			smoothScrollIntoViewIfNeeded(commentPermalinkContainer, {
				scrollMode: 'always',
				behavior: 'smooth',
			});
			setScrollStatus(true);
			const analyticsEvent = createAnalyticsEvent({
				action: 'scrolled',
			});
			fireUIAnalytics(analyticsEvent, 'permalink comment', { name: 'comment' });
		}
		// scroll to unread comment during real time updates
		if (
			(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
			commentScrollStatusForRealTimeUpdates &&
			commentId === topMostUnreadCommentId &&
			isUnreadComment &&
			expVal('thor_issue_view_realtime_updates_experiment', 'isEnabled', false)
		) {
			smoothScrollIntoViewIfNeeded(unreadCommentElement, {
				scrollMode: 'always',
				behavior: 'smooth',
			});
			setScrollStatusForRealTimeUpdate?.(false);
		}
	}, [
		commentId,
		commentPermalinkContainer,
		commentScrollStatusForRealTimeUpdates,
		createAnalyticsEvent,
		hasScrolledPermalink,
		isUnreadComment,
		projectType,
		setScrollStatus,
		setScrollStatusForRealTimeUpdate,
		shouldScrollAfterAsyncComponentsLoaded,
		topMostUnreadCommentId,
		unreadCommentElement,
	]);
};
export const useFireHiglightedCommentAnalytics = () => {
	const { createAnalyticsEvent, isHighlighted, parentId } = useCommentContext();
	useEffect(() => {
		if (isHighlighted) {
			const analyticsEvent = createAnalyticsEvent({});
			fireOperationalAnalytics(analyticsEvent, 'focussed comment', {
				isParent: parentId,
			});
		}
	}, [createAnalyticsEvent, isHighlighted, parentId]);
};
export const useEditSave = () => {
	const {
		author,
		commentId,
		createdDate,
		bodyHtml,
		shouldShowCommentVisibility,
		visibility,
		visibilityEdited,
		getOnEditSave,
		createAnalyticsEvent,
		jsdIncidentActivityViewHidden,
		fullIssueUrl,
		isIssueOfIncidentsPractice,
		isEditingInternal,
		projectType,
		parentId,
		commentSessionId,
		isRealTimeCommentUpdated,
		selectedEventOccurredAt,
	} = useCommentContext();
	const onEditSave = useCallback(
		(value: ADF): void => {
			const isValueEmpty = isOnlyWhitespaceAdf(value);
			if (!isValueEmpty) {
				const authorId = author ? author.id : null;
				const analyticsEvent = createAnalyticsEvent({ action: 'updated' });
				selectedEventOccurredAt
					? analyticsEvent.update({ commentEditWithEventOccurredAt: true })
					: analyticsEvent.update({ commentEditWithEventOccurredAt: false });
				if (shouldShowCommentVisibility) {
					analyticsEvent.update({
						isVisibilityUpdated: !areCommentVisibilitiesEqual(visibility, visibilityEdited),
					});
				}
				if (fg('thor_send_realtime_attribute')) {
					analyticsEvent.update({
						isRealTimeCommentUpdated,
					});
				}
				getOnEditSave(
					{
						authorId,
						createdDate,
						bodyAdf: value,
						bodyHtml,
						isNewComment: commentId.includes(NEW_COMMENT_ID),
						visibility:
							projectType === SERVICE_DESK_PROJECT && !isEditingInternal
								? COMMENT_VISIBILITY_PUBLIC
								: visibilityEdited,
						...(isIssueOfIncidentsPractice && isEditingInternal
							? {
									eventOccurredAt: selectedEventOccurredAt,
									jsdIncidentActivityViewHidden,
									fullIssueUrl,
									triggerIncidentSaveCommentFlag: isIssueOfIncidentsPractice,
								}
							: {}),
						parentId,
					},
					analyticsEvent,
					commentSessionId,
				);
			}
		},
		[
			author,
			bodyHtml,
			commentId,
			commentSessionId,
			createAnalyticsEvent,
			createdDate,
			fullIssueUrl,
			getOnEditSave,
			isEditingInternal,
			isIssueOfIncidentsPractice,
			isRealTimeCommentUpdated,
			jsdIncidentActivityViewHidden,
			parentId,
			projectType,
			selectedEventOccurredAt,
			shouldShowCommentVisibility,
			visibility,
			visibilityEdited,
		],
	);
	return onEditSave;
};
export const useHideSave = () => {
	const {
		author,
		commentId,
		createdDate,
		bodyHtml,
		bodyAdf,
		jsdIncidentActivityViewHidden,
		visibilityEdited,
		projectType,
		isEditingInternal,
		getOnEditSave,
		createAnalyticsEvent,
		eventOccurredAt,
		commentSessionId,
	} = useCommentContext();
	const onHideSave = useCallback(() => {
		const authorId = author ? author.id : null;
		const analyticsEvent = createAnalyticsEvent({ action: 'updated' });
		fireUIAnalytics(
			createAnalyticsEvent({}),
			jsdIncidentActivityViewHidden ? 'unhide clicked' : 'hide clicked',
		);
		getOnEditSave(
			{
				authorId,
				createdDate,
				bodyAdf,
				bodyHtml,
				isNewComment: commentId.includes(NEW_COMMENT_ID),
				visibility:
					projectType === SERVICE_DESK_PROJECT && !isEditingInternal
						? COMMENT_VISIBILITY_PUBLIC
						: visibilityEdited,
				eventOccurredAt,
				jsdIncidentActivityViewHidden: !jsdIncidentActivityViewHidden,
			},
			analyticsEvent,
			commentSessionId,
		);
	}, [
		author,
		bodyAdf,
		bodyHtml,
		commentId,
		commentSessionId,
		createAnalyticsEvent,
		createdDate,
		eventOccurredAt,
		getOnEditSave,
		isEditingInternal,
		jsdIncidentActivityViewHidden,
		projectType,
		visibilityEdited,
	]);
	return onHideSave;
};
export const useCommentInteraction = () => {
	const { projectType, onCommentInteractionUpdateCommentViewInteractionTimeMarker } =
		useCommentContext();
	const isUnreadComment = useUnreadComment();
	const onCommentInteraction = useCallback(() => {
		if (
			(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
			fg('thor_issue_view_realtime_updates_gate')
		) {
			isUnreadComment &&
				onCommentInteractionUpdateCommentViewInteractionTimeMarker &&
				onCommentInteractionUpdateCommentViewInteractionTimeMarker();
		}
	}, [isUnreadComment, onCommentInteractionUpdateCommentViewInteractionTimeMarker, projectType]);
	return onCommentInteraction;
};
export const useReply = () => {
	const {
		onReplyClick,
		parentId,
		commentId,
		author,
		shouldShowCommentVisibility,
		visibility,
		createAnalyticsEvent,
		hasReplies,
		showCommentSlackFlag,
		onShowFlagForSlackSync,
	} = useCommentContext();
	const authorDisplayName = useAuthorDisplayName();
	const onCommentInteraction = useCommentInteraction();

	const onReplyWithExtraCommentContent = (extraCommentContent?: ADF) => {
		if (!onReplyClick) {
			return;
		}
		if (showCommentSlackFlag && onShowFlagForSlackSync) {
			onShowFlagForSlackSync();
		}

		const replyingToCommentId = parentId || commentId;
		const authorId = author ? author.id : null;
		const replyVisibility = shouldShowCommentVisibility ? visibility : undefined;
		const commentSessionId = uuid();

		onCommentInteraction();
		onReplyClick(
			replyingToCommentId,
			authorId,
			authorDisplayName,
			replyVisibility,
			commentSessionId,
			extraCommentContent &&
				expVal('work_item_create_from_comment_and_pin_comment', 'hasWorkItemCreate', false)
				? extraCommentContent
				: undefined,
		);

		fireUIAnalytics(
			createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' }),
			'replyButton',
			{
				isRootComment: !!parentId,
				hasReplies,
				commentSessionId,
				// This attribute is used to track if the reply button is auto open with a template filled in
				...(extraCommentContent && { isTriggeredByCommentTemplate: true }),
			},
		);
	};
	return onReplyWithExtraCommentContent;
};
export const useDelete = () => {
	const { parentId, createAnalyticsEvent, hasReplies } = useCommentContext();
	const onDeleteClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' }),
			'deleteCommentButton',
			{
				isRootComment: !!parentId,
				hasReplies,
			},
		);
	};
	return onDeleteClick;
};
export const useEdit = () => {
	const { getOnEditClick, isInternal, hasDraft, createAnalyticsEvent } = useCommentContext();
	const onCommentInteraction = useCommentInteraction();
	const onEditClick = useCallback(
		(_: MouseEvent<HTMLElement>, analyticsEvent?: UIAnalyticsEvent, isSiteAdmin?: boolean) => {
			const commentSessionId = uuid();
			getOnEditClick(isInternal, commentSessionId);
			onCommentInteraction();
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			fireOperationalAnalytics(analyticsEvent as UIAnalyticsEvent, 'editCommentEditor expanded', {
				hasDraft,
			});
			const jsmLoomExperiment = isJsmLoomExperimentEnabled();
			fireUIAnalytics(createAnalyticsEvent({ actionSubject: 'commentEdit', action: 'started' }), {
				hasDraft,
				commentSessionId,
				...(jsmLoomExperiment?.enabled && {
					isSiteAdmin,
					loomJsmAgentExperimentCohort: jsmLoomExperiment?.cohort,
				}),
			});
		},
		[createAnalyticsEvent, getOnEditClick, hasDraft, isInternal, onCommentInteraction],
	);
	return onEditClick;
};

export const useAuthor = () => {
	const { author } = useCommentContext();
	const { formatMessage } = useIntl();
	const anonymousUser: User = {
		id: null,
		...author,
		displayName: formatMessage(messages.anonymous),
	};

	if (!author) {
		return anonymousUser;
	}
	return author;
};

export const useIssueCreate = () => {
	const { author, currentUserAccountId, bodyAdf } = useCommentContext();
	const { formatMessage } = useIntl();

	const authorId = author ? author.id : null;
	const authorDisplayName = useAuthorDisplayName();
	const onReplyWithExtraCommentContent = useReply();

	const onIssueCreate = (data: CreatedIssueWithIssueData) => {
		const issueUrl = createFullIssueUrl('', data.issueKey);

		const quotedCommentAndIssue = [p(inlineCard({ url: issueUrl })), rule(), ...bodyAdf.content];

		const extraCommentContent =
			currentUserAccountId === authorId
				? doc(p(text(formatMessage(messages.createdIssueBySelf))), ...quotedCommentAndIssue)
				: doc(
						p(
							authorId
								? mention({
										id: authorId,
										text: `@${authorDisplayName}`,
									})
								: text(''),
							text(' '),
							text(formatMessage(messages.createdIssue)),
						),

						...quotedCommentAndIssue,
					);

		onReplyWithExtraCommentContent(extraCommentContent);
	};
	return onIssueCreate;
};

export const useReplyToComment = () => {
	const { projectType, canAdd, isThreadedCommentsEnabled, isHighlighted } = useCommentContext();
	const onReplyClick = useReply();

	const [replyToCommentParamValue, setReplyToCommentParamValue] = useQueryParam(ACTION_KEY_PARAM);
	const isReplyToCommentParamSet = replyToCommentParamValue === REPLY_TO_COMMENT_ACTION_TYPE;
	const isTarget =
		(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
		canAdd &&
		isThreadedCommentsEnabled;

	useEffect(() => {
		if (!fg('reply_on_comment_notification_gate')) {
			return;
		}
		if (!isHighlighted) {
			return;
		}
		if (isReplyToCommentParamSet && !isTarget) {
			setReplyToCommentParamValue(undefined);
			return;
		}
		if (
			isReplyToCommentParamSet &&
			isTarget &&
			expVal('reply_on_comment_notification', 'isEnabled', false)
		) {
			onReplyClick();
			setReplyToCommentParamValue(undefined);
		}
	}, [
		canAdd,
		isHighlighted,
		isReplyToCommentParamSet,
		isTarget,
		isThreadedCommentsEnabled,
		projectType,
		onReplyClick,
		setReplyToCommentParamValue,
	]);
};
